import { showCustomToast } from 'common/components/CustomToast/utils';
import { useMutation } from '@apollo/client';
import {
  ContextualMenu,
  DayOfWeek,
  DefaultButton,
  Label,
  Modal,
  PrimaryButton,
  ProgressIndicator,
  Stack,
  Text,
} from '@fluentui/react';
import { useBoolean } from '@fluentui/react-hooks';
import { yupResolver } from '@hookform/resolvers/yup';
import { CloseButton } from 'common/components/Buttons';
import { ConfirmDialog } from 'common/components/ConfirmDialog';
import {
  FormHookDatePicker,
  FormHookDropdown,
  FormHookTextField,
} from 'common/components/FormHooksFields';
import {
  EntityEnvironmentalDelete,
  EntityEnvironmentalDeleteVariables,
} from 'common/graphql/__generated__/EntityEnvironmentalDelete';
import {
  EntityEnvironmentalUpdate,
  EntityEnvironmentalUpdateVariables,
} from 'common/graphql/__generated__/EntityEnvironmentalUpdate';
import {
  EntityEnvironmentalItemUpdateTypeInput,
  TransactionLayout,
} from 'common/types/globalTypes';
import { dateFormat } from 'common/utils/dateFormats';
import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { CategoryOptionsType } from '..';
import { EnvironmentalItemsView } from '../EnvironmentalItemsView';
import {
  EntityEnvironmentalDocumentCreate,
  EntityEnvironmentalDocumentCreateVariables,
} from '../__generated__/EntityEnvironmentalDocumentCreate';
import { validationSchema } from '../carbonAccountingValidation';
import { CarbonAccountingValues } from '../types';
import { useStyles } from './index.styles';
import { getDefaultValues } from './utils';
import ENTITY_ENVIRONMENT_DOCUMENT_CREATE from '../EntityEnvironmentalDocumentCreate.graphql';
import ENTITY_ENVIRONMENT_DELETE from 'common/graphql/EntityEnvironmentalDelete.graphql';
import ENTITY_ENVIRONMENT_UPDATE from 'common/graphql/EntityEnvironmentalUpdate.graphql';
import { GetEntityDocument_entityDocument_entityEnvironmentalsByEntityId } from '../../../__generated__/GetEntityDocument';
const CONFIRM_DELETE_DIALOG_TITLE = 'Please confirm the action';
const CONFIRM_DELETE_DIALOG_SUBTEXT =
  'This will remove the accounting from this transaction';

interface FormModalProps {
  entityId: string;
  isEnvironmentalsExist: boolean;
  categoryOptions: CategoryOptionsType[];
  entityDocumentDetails:
  | GetEntityDocument_entityDocument_entityEnvironmentalsByEntityId
  | undefined;
  environmentDeletedUpdated: (isdeleted: boolean) => void;
  setOpen: (open: boolean) => void;
}

export const FormModal: React.FC<FormModalProps> = ({
  entityId,
  isEnvironmentalsExist,
  categoryOptions,
  entityDocumentDetails,
  environmentDeletedUpdated,
  setOpen,
}) => {
  const styles = useStyles();

  const [categoryId, setCategoryId] = useState<string | undefined>();
  const [layoutType, setLayoutType] = useState<TransactionLayout | undefined>();
  const [categoryName, setCategoryName] = React.useState<string | undefined>();
  const [hideConfirmDialog, { toggle: toggleConfirmDialog }] = useBoolean(true);

  const [
    entityEnvironmentalDocumentCreate,
    { loading: entityEnvironmentalDocumentCreateLoading },
  ] = useMutation<
    EntityEnvironmentalDocumentCreate,
    EntityEnvironmentalDocumentCreateVariables
  >(ENTITY_ENVIRONMENT_DOCUMENT_CREATE, { errorPolicy: 'all' });

  const [deleteEnvironment, { loading: loadingDeleteEnvironment }] =
    useMutation<EntityEnvironmentalDelete, EntityEnvironmentalDeleteVariables>(
      ENTITY_ENVIRONMENT_DELETE,
      { errorPolicy: 'all' }
    );

  const [updateEnvironment, { loading: updateEnvironmentLoading }] =
    useMutation<EntityEnvironmentalUpdate, EntityEnvironmentalUpdateVariables>(
      ENTITY_ENVIRONMENT_UPDATE,
      { errorPolicy: 'all' }
    );

  const formMethods = useForm<CarbonAccountingValues>({
    mode: 'all',
    resolver: yupResolver(validationSchema()),
  });

  const {
    handleSubmit,
    reset,
    trigger,
    formState: { errors, isDirty, isSubmitting },
  } = { ...formMethods };

  const entityEnvironmentalDelete = async () => {
    const { errors } = await deleteEnvironment({
      variables: {
        input: {
          entityDelete: [
            {
              id: entityDocumentDetails?.nodes[0].id!,
              rowTimestamp: entityDocumentDetails?.nodes[0]._rowTimestamp!,
            },
          ],
        },
      },
    });
    if (errors?.length)
      showCustomToast({
        content: errors[0].message,
        appearance: 'error',
      });
    else {
      showCustomToast({
        content: 'Environmental deleted successfully',
        appearance: 'success',
      });
      setCategoryId(undefined);
      setOpen(false);
      environmentDeletedUpdated(true);
    }
  };

  const onHandleSubmit = async (values: CarbonAccountingValues) => {
    if (isEnvironmentalsExist && categoryId) {
      const itemUpdateFilter: EntityEnvironmentalItemUpdateTypeInput[] = [];
      if (!!values.entityEnvironmentalItems?.length) {
        values.entityEnvironmentalItems?.map((item) => {
          if (item.id) {
            const {
              id,
              isIntegerFormat,
              isFloatFormat,
              isAmountFormat,
              ...itemFields
            } = { ...item };
            const environmentalItemObj: EntityEnvironmentalItemUpdateTypeInput =
            {
              id: item.id,
              rowTimestamp: entityDocumentDetails?.nodes[0]._rowTimestamp,
              entityEnvironmentalItemPatch: {
                ...(Object.fromEntries(
                  Object.entries(itemFields).filter(([key]) => key !== '__typename')
                )),
              },
            };

            itemUpdateFilter.push(environmentalItemObj);
          }
        });
      }
      const { errors } = await updateEnvironment({
        variables: {
          input: {
            id: entityDocumentDetails?.nodes[0].id!,
            rowTimestamp: entityDocumentDetails?.nodes.length
              ? entityDocumentDetails.nodes[0]._rowTimestamp!
              : '',
            entityEnvironmentalPatch: {
              description: values.description,
              startDate: values.startDate ? dateFormat(values.startDate) : null,
              endDate: values.endDate ? dateFormat(values.endDate) : null,
            },
            entityEnvironmentalItemsUpdate: itemUpdateFilter,
          },
        },
      });
      if (errors?.length)
        showCustomToast({
          content: errors[0].message,
          appearance: 'error',
        });
      else {
        showCustomToast({
          content: 'Environmental updated successfully',
          appearance: 'success',
        });
        setOpen(false);
        environmentDeletedUpdated(true);
      }
    } else {
      const { errors } = await entityEnvironmentalDocumentCreate({
        variables: {
          input: {
            entityEnvironmental: {
              description: values.description,
              entityId: entityId,
              environmentalMetricId: values.environmentalMetricId!,
              startDate: values.startDate ? dateFormat(values.startDate) : null,
              endDate: values.endDate ? dateFormat(values.endDate) : null,
            },
            entityEnvironmentalItems: values.entityEnvironmentalItems?.map(
              ({ isIntegerFormat, isFloatFormat, isAmountFormat, ...rest }) =>
                rest
            ),
          },
        },
      });
      if (errors?.length)
        showCustomToast({
          content: errors[0].message,
          appearance: 'error',
        });
      else {
        showCustomToast({
          content: 'Environmental added successfully',
          appearance: 'success',
        });
        setOpen(false);
        environmentDeletedUpdated(true);
      }
    }
  };

  useEffect(() => {
    if (entityDocumentDetails?.nodes.length! > 0) {
      setCategoryId(entityDocumentDetails?.nodes[0].environmentalMetricId!);
      setLayoutType(entityDocumentDetails?.nodes[0].environmentalMetric?.layoutType!);
      setCategoryName(
        entityDocumentDetails?.nodes[0].environmentalMetric?.environmentalMetric!
      );
    }
  }, [entityDocumentDetails]);

  useEffect(() => {
    const defaultValues = getDefaultValues({
      entityDocument: entityDocumentDetails,
    });
    reset(defaultValues);
    trigger();
  }, [entityDocumentDetails, reset, trigger]);

  return (
    <Modal
      isOpen
      styles={{
        scrollableContent: {
          overflowY: 'none',
        },
      }}
      dragOptions={{
        moveMenuItemText: 'Move',
        closeMenuItemText: 'Close',
        menu: ContextualMenu,
        dragHandleSelector: '.ms-Modal-scrollableContent > div:first-child',
      }}
    >
      <div className={styles.header}>
        <Stack tokens={{ childrenGap: 10 }} horizontal>
          <Text variant={'xLarge'}>Carbon accounting</Text>
          {isEnvironmentalsExist && categoryName && (
            <Text variant={'xLarge'} className={styles.accountingType}>
              {categoryName}
            </Text>
          )}
        </Stack>
        <Stack>
          <CloseButton
            onClick={() => {
              reset();
              setOpen(false);
            }}
          />
        </Stack>
      </div>
      <FormProvider {...formMethods}>
        <Stack className={styles.mainContainer} tokens={{ childrenGap: 20 }}>
          <Stack className={styles.formContainer} tokens={{ childrenGap: 20 }}>
            <FormHookDropdown
              label="Category"
              placeholder="Select"
              options={categoryOptions}
              name="environmentalMetricId"
              onChange={(_event, option) => {
                const selectedOption = option as CategoryOptionsType;
                setCategoryId(selectedOption.key.toString());
                setLayoutType(selectedOption.layoutType!);
              }}
              disabled={isEnvironmentalsExist}
              required
            />
            <Stack>
              <Label>Time period</Label>
              <Stack horizontal tokens={{ childrenGap: 20 }}>
                <Stack.Item grow={1}>
                  <FormHookDatePicker
                    name="startDate"
                    placeholder="From date..."
                    ariaLabel="From date"
                    firstDayOfWeek={DayOfWeek.Monday}
                    showWeekNumbers
                    firstWeekOfYear={1}
                    showMonthPickerAsOverlay
                    showGoToToday
                  />
                </Stack.Item>
                <Stack.Item grow={1}>
                  <FormHookDatePicker
                    name="endDate"
                    placeholder="To date..."
                    ariaLabel="To date"
                    firstDayOfWeek={DayOfWeek.Monday}
                    showWeekNumbers
                    firstWeekOfYear={1}
                    showMonthPickerAsOverlay
                    showGoToToday
                  />
                </Stack.Item>
              </Stack>
            </Stack>
            <FormHookTextField
              name="description"
              label="Comment"
              placeholder="Enter a comment"
            />
          </Stack>
          {categoryId && layoutType && (
            <EnvironmentalItemsView
              categoryId={categoryId}
              layoutType={layoutType}
              isEnvironmentalsExist={isEnvironmentalsExist}
            />
          )}
          {(entityEnvironmentalDocumentCreateLoading ||
            loadingDeleteEnvironment ||
            updateEnvironmentLoading) && <ProgressIndicator />}
          <ConfirmDialog
            hidden={hideConfirmDialog}
            title={CONFIRM_DELETE_DIALOG_TITLE}
            subText={CONFIRM_DELETE_DIALOG_SUBTEXT}
            onDismiss={toggleConfirmDialog}
            onConfirm={() => {
              toggleConfirmDialog();
              entityEnvironmentalDelete();
            }}
          />
          <Stack horizontal horizontalAlign="end" tokens={{ childrenGap: 20 }}>
            {!!entityDocumentDetails?.nodes.length && (
              <PrimaryButton
                onClick={() => toggleConfirmDialog()}
                text="Delete"
              />
            )}
            <PrimaryButton
              disabled={
                !isDirty || Object.keys(errors).length > 0 || isSubmitting
              }
              onClick={handleSubmit(onHandleSubmit)}
              text="Save"
            />
            <DefaultButton
              onClick={() => {
                reset();
                setOpen(false);
              }}
              text="Cancel"
            />
          </Stack>
        </Stack>
      </FormProvider>
    </Modal>
  );
};
